// libraries.
import { useSelector } from 'react-redux';
import { robotoRegular } from '@habitech/shared';
import { useEffect, useState } from 'react';

// hooks.
import { getPricingTasks } from '@domains/documents-review/store/slices/document-review-slice';
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import { useDocumentsTaskFilters } from '../../DocumentsAuditDashboard/components/DocumentsTable/hooks/useDocumentsTaskFilters';
import { useFetchingDocumentsTasks } from '../../DocumentsAuditDashboard/components/DocumentsTable/hooks/useFetchingDocumentsTasks';

// components.
import { DataTable } from '@shared/DataTable/DataTable';
import { statusLabel } from '../../DocumentsAuditDashboard/components/DocumentsTable/utils';
import { SelectTasks } from './SelectTasks';
import { PendingTasks } from '../../DocumentsAuditDashboard/components/DocumentsTable/PendingTasks';
import { DurationCell } from '../../DocumentsAuditDashboard/components/DocumentsTable/components/TaskDuration/DurationCell';
import { BusinessType } from '../../DocumentsAuditDashboard/components/DocumentsTable/components/BusinessType/BusinessType';
import { DropdownFilter } from '@shared/DropdownFilter/DropdownFilter';
import { SortDataFilter } from '@shared/SortDataFilter/SortDataFilter';
import { ReassignSelectedTasks } from './ReassignSelectedTasks';

// styles.
import { WrapperTable } from './styles';

export const ReassignTaskTable = () => {
  const email = useSelector(getEmail);
  const taskList = useSelector(getPricingTasks) || [];
  const country = useSelector(getCountry) as 'MX' | 'CO';

  const [filterAgent, setFilterAgent] = useState(false);

  const {
    filteredData,
    sortOrder,
    toggleSortOrder,
    agentOptions,
    actionOptions,
    businessTypeOptions,
    handleFilterChange,
    filters,
  } = useDocumentsTaskFilters(taskList, country);

  const { isFetching, refetch } = useFetchingDocumentsTasks({
    status: filters.action.join(','),
    business_type: filters.business_type.join(','),
  });

  useEffect(() => {
    if (!filterAgent && agentOptions.length > 0) {
      const allAgents = agentOptions.map(opt => opt.value);
      const isEmailInList = email && allAgents.includes(email);

      // If the user's email is one of the agents, pre-filter to their tasks.
      // Otherwise leave the filter empty so ALL tasks are visible — picking
      // "todos" should never hide rows assigned outside the hardcoded list.
      handleFilterChange('agent', isEmailInList ? [email] : []);
      setFilterAgent(true);
    }
  }, [email, agentOptions, filterAgent, handleFilterChange]);

  if (isFetching) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100 fw-bold"
        style={{ fontFamily: robotoRegular }}
      >
        Cargando...
      </div>
    );
  }

  return (
    <WrapperTable>
      <div className="d-flex justify-content-between">
        <PendingTasks tasks={taskList} />
        <ReassignSelectedTasks refetch={refetch} />
      </div>
      <div className="table-scroll">
        <DataTable
          headers={[
            '#',
            '',
            {
              label: 'Fecha',
              filter: (
                <SortDataFilter order={sortOrder} onToggle={toggleSortOrder} />
              ),
            },
            'SLA',
            'NID',
            {
              label: 'Línea de negocio',
              filter: (
                <DropdownFilter
                  value={filters.business_type}
                  options={businessTypeOptions}
                  onChange={selected =>
                    handleFilterChange('business_type', selected)
                  }
                />
              ),
            },
            'Comentario',
            {
              label: 'Agente',
              filter: (
                <DropdownFilter
                  value={filters.agent}
                  options={agentOptions}
                  onChange={selected => handleFilterChange('agent', selected)}
                />
              ),
            },
            {
              label: 'Estado',
              filter: (
                <DropdownFilter
                  value={filters.action}
                  options={actionOptions}
                  onChange={selected => handleFilterChange('action', selected)}
                />
              ),
            },
          ]}
          data={filteredData}
          renderRow={(row, index) => (
            <tr key={row.id}>
              <td>{index + 1}</td>
              <td>
                <SelectTasks rowTask={row} />
              </td>
              <td>{row.date}</td>
              <td>
                <DurationCell
                  startAt={row.date}
                  updatedAt={row.updated_at}
                  country={country}
                  action={row.action}
                />
              </td>
              <td>{row.nid}</td>
              <td>
                <BusinessType businessType={row.business_type} />
              </td>
              <td className="comment-cell">
                {row.comments || 'Sin comentario'}
              </td>
              <td>{row.agent || 'Sin asignar'}</td>
              <td>{statusLabel[row.action]}</td>
            </tr>
          )}
        />
      </div>
    </WrapperTable>
  );
};
